import styled, { css, keyframes } from 'styled-components';
import colors from 'styles/colors';
import sizes from 'styles/sizes';

import { Typography } from '@mui/material';
import { BrandCode } from 'types';

import {
	BACKGROUND_ANIMATION_DELAY,
	BACKGROUND_CLOSE_ANIMATION_DURATION,
	BACKGROUND_OPEN_ANIMATION_DURATION,
	BACKGROUND_OPEN_OPACITY,
	LIST_ANIMATION_DELAY,
	LIST_ANIMATION_DURATION,
	LIST_ANIMATION_STAGGER,
	LIST_ITEM_OFFSET,
	MODAL_ANIMATION_DURATION,
} from './constants';

export const Container = styled.div`
	width: 100%;
	max-width: max-content;
	z-index: unset;
	@media (min-width: ${sizes.media.smallMaxWidth}px) {
		width: unset;
	}
`;

export const DropdownContainer = styled.div`
	position: relative;
`;

export const Select = styled(Typography)`
	position: relative;
	padding: 15px 0px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	border-radius: 5px;
	background: ${colors.primary.oceanLight};
	color: ${colors.secondary.deepCove};
	outline: transparent;

	&& p {
		margin-right: 6px;
		color: ${({ palette }) => palette.primary.dark};
		${({ styles }) => styles}
		${({ theme }) =>
			({
				[BrandCode.RoyalCaribbean]: `
          color: ${colors.rccl.dropDownLabel};
          font-family: ${theme.typography.fontFamilySemiBold};
          svg {
            path {
              stroke: ${colors.rccl.dropDownLabel};
            }
          }
        `,
				[BrandCode.Celebrity]: `
          color: ${colors.celebrity.dropDownLabel};
          font-family: ${theme.typography.fontFamilySemiBold};
          svg {
            path {
              stroke: ${colors.rccl.dropDownLabel};
            }
          }
        `,
			})[theme.brandCode]}
	}

	transition:
		background-color 0.2s cubic-bezier(0, 0, 0, 0),
		color 0.2s cubic-bezier(0, 0, 0, 0);
	&:hover {
		background-color: ${colors.secondary.deepCove};
		color: ${colors.primary.ice};

		svg {
			path {
				stroke: ${colors.primary.ice};
			}
		}
	}

	@media (min-width: ${sizes.media.smallMaxWidth}px) {
		width: unset;
	}

	&:disabled {
		pointer-events: none;
		background: #edeff2;

		svg {
			opacity: 0.2;
		}
	}

	&.btn-style-text {
		background: none;
		color: ${colors.primary.ocean};
		svg {
			path {
				stroke: ${({ palette }) => palette.primary.main};
			}
		}

		${({ theme }) =>
			({
				[BrandCode.RoyalCaribbean]: `
          background: ${colors.rccl.dropDownBackground};
          color: ${colors.rccl.dropDownLabel};
          padding: 10px 16px;
          svg {
            path {
              stroke: ${colors.rccl.dropDownLabel};
            }
          }
        `,
				[BrandCode.Celebrity]: `
          background: ${colors.celebrity.dropDownBackground};
          color: ${colors.celebrity.dropDownLabel};
          padding: 10px 16px;
          svg {
            path {
              stroke: ${colors.celebrity.dropDownLabel};
            }
          }
        `,
			})[theme.brandCode]}

		@media (min-width: ${sizes.media.tabletLandscape}px) {
			&:hover {
				background: ${colors.rccl.dropDownBackgroundHover};
				p {
					color: ${({ palette }) => palette.secondary.main};
				}
				svg {
					path {
						stroke: ${({ palette }) => palette.secondary.main};
					}
				}
			}
		}
	}
	&.open {
		${({ theme }) =>
			({
				[BrandCode.RoyalCaribbean]: `
        	background: ${colors.secondary.deepCove};
        `,
				[BrandCode.Celebrity]: `
     			background: ${colors.celebrity.dropDownLabel};
        `,
			})[theme.brandCode]}
		p {
			color: ${colors.primary.ice};
		}
		svg {
			path {
				stroke: ${colors.primary.ice};
			}
		}
	}
`;

export const Modal = styled.div`
	height: auto;
	overflow: hidden;
	position: absolute;
	bottom: -10px;
	transform: translateY(100%);
	z-index: 100;
	min-width: max-content;
	max-height: ${({ open, lines }) => (open ? `${lines * 48 + 48}px` : '0')};
	transition: max-height ${MODAL_ANIMATION_DURATION}ms ease-out;

	${({ anchorLeft }) =>
		anchorLeft
			? css`
					right: unset;
					left: 0;
				`
			: css`
					right: 0;
					left: unset;
				`}
`;

export const ModalDropdown = styled.div`
	min-width: max-content;
	padding: 24px 30px 24px 16px;
	background: ${colors.primary.ice};
	border-radius: 8px;

	@media (min-width: ${sizes.media.smallMaxWidth}px) {
		top: 60px;
	}

	@media (min-width: ${sizes.media.mediumMaxWidth}px) {
		top: 40px;
	}
`;

export const List = styled.ul``;

export const Item = styled.li`
	display: flex;
	gap: 12px;
	align-items: center;
	margin-bottom: 24px;
	cursor: ${(props) => (props.disabled ? 'context-menu' : 'pointer')};
	opacity: ${({ open }) => (open ? 1 : 0)};
	transform: translateY(${({ open }) => (open ? '0' : LIST_ITEM_OFFSET)});
	transition:
		opacity ${LIST_ANIMATION_DURATION}ms ease-out,
		transform ${LIST_ANIMATION_DURATION}ms ease-out;
	transition-delay: ${({ index }) => `calc(${LIST_ANIMATION_DELAY}ms + ${index} * ${LIST_ANIMATION_STAGGER}ms)`};

	p {
		flex: 2;
		color: ${({ disabled, palette }) => (disabled ? colors.functional.middleGray : palette.secondary.main)};
		max-width: 60vw;
	}

	img {
		flex: 1;
	}

	&:last-child {
		margin-bottom: 0;
	}

	&:hover {
		.img-container {
			border-color: ${colors.secondary.deepCove};
		}
		p {
			color: ${({ disabled, palette }) => (disabled ? colors.functional.middleGray : palette.primary.main)};
		}
		svg circle:first-of-type {
			stroke: ${({ disabled, palette }) => (disabled ? colors.functional.middleGray : palette.primary.main)};
		}
	}
`;

const bgFadeIn = keyframes`
  from { 
		display: none;
		opacity: 0;
		z-index: -1;

	}
  to { 
		display: block;
		opacity: 1;
		z-index: 10;
		}
`;

const bgFadeOut = keyframes`
  from {
		display: block;
		opacity: 1;
		z-index: 10;
	}
  to {
		display: none;
		opacity: 0;
		z-index: -1;
	}
`;

export const Background = styled.div`
	position: fixed;
	top: -100vw;
	left: -100vw;
	height: 400vh;
	width: 200vw;
	background-color: rgba(0, 0, 0, ${BACKGROUND_OPEN_OPACITY});
	z-index: -1;
	opacity: 0;
	animation-name: ${({ open }) => (open ? bgFadeIn : bgFadeOut)};
	animation-duration: ${({ open }) => (open ? BACKGROUND_OPEN_ANIMATION_DURATION : BACKGROUND_CLOSE_ANIMATION_DURATION)}ms;
	animation-delay: ${({ open }) => (open ? BACKGROUND_ANIMATION_DELAY : 0)}ms;
	animation-fill-mode: forwards;
`;
